// ============================================================
// PiSettlementDefaultsPanel.tsx — Admin › PI settlements (Stage 4 round 2a).
// The firm's CFA defaults, copied onto each NEW CFA (an existing CFA keeps its
// own terms). Same pattern as Admin › Branding: drafts until "Save defaults",
// diff-only PATCH, server re-validates (admin-only route) and audits every
// change, unsaved-changes guard, Discard. Half-typed invalid text counts as
// an unsaved change (so leaving warns); every input is locked while saving.
// Each setting has a one-line plain-English explanation, and the option that
// deducts LESS from the client is marked (every Yes/No default is that one).
// (Batch 2) Also the firm's statement chase: "Chase the client’s answer after
// N days" (statementChaseDays, 1–90, default 14) — a 400 from the server on
// that field shows under it.
// ============================================================
import React, { useEffect, useState } from 'react'
import { getSettlementDefaultsApi, patchSettlementDefaultsApi } from '../../api/casevoApi'
import { UnsavedGuard } from '../../hooks/useUnsavedChangesGuard'
import {
  SettingsCard, SettingsToggleRow, SettingsSwitch, SettingsSaved, SIcon, sInput,
  S_BTN_PRIMARY, S_BTN_GHOST, S_BTN_GHOST_SM, S_LABEL, S_NOTE, S_FIELD_ERROR, S_FORMFOOT,
} from '../settings/settingsUi'
import {
  CAP_BASE_LABEL, CFA_STAGES, CFA_STAGE_LABEL, CFA_TERM_SPECS, DEDUCTION_LABEL, bpToPercentText, percentTextToBp,
  type CfaStage, type DeductionCapBase, type DeductionItem, type SettlementDefaults, type StageRate,
} from '../../types/settlement.types'

// The defaults' keys for the eight Yes/No CFA terms.
const termKey = (k: string): keyof SettlementDefaults =>
  (`default${k.charAt(0).toUpperCase()}${k.slice(1)}`) as keyof SettlementDefaults

// "Deducts less" marker beside the option that takes less from the client.
export const DeductsLess: React.FC<{ isDefault?: boolean }> = ({ isDefault }) => (
  <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-[2px] rounded-full bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8] whitespace-nowrap">
    <SIcon name="check" size={11} />{isDefault ? 'Default · deducts less' : 'Deducts less'}
  </span>
)

// Stage-rate text boxes ↔ StageRate[] (blank = falls back to the stage before).
export type StageDraft = Record<CfaStage, string>
export const stageDraftOf = (stages: StageRate[]): StageDraft =>
  Object.fromEntries(CFA_STAGES.map((s) => [s, bpToPercentText(stages.find((x) => x.stage === s)?.rateBasisPoints)])) as StageDraft
export const stagesOfDraft = (d: StageDraft): StageRate[] | null => {
  const out: StageRate[] = []
  for (const s of CFA_STAGES) {
    const bp = percentTextToBp(d[s])
    if (bp === undefined) return null
    if (bp !== null) out.push({ stage: s, rateBasisPoints: bp })
  }
  return out
}

// Staged-rate editor (also used by the case's CFA panel).
export const StageRatesEditor: React.FC<{ draft: StageDraft; onChange: (d: StageDraft) => void; disabled?: boolean; idPrefix: string }> = ({ draft, onChange, disabled, idPrefix }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-[10px]">
    {CFA_STAGES.map((s) => {
      const bad = percentTextToBp(draft[s]) === undefined
      return (
        <div key={s}>
          <label className={S_LABEL} htmlFor={`${idPrefix}-${s}`}>{CFA_STAGE_LABEL[s]}</label>
          <div className="flex items-center gap-[6px]">
            <input id={`${idPrefix}-${s}`} className={`${sInput(bad)} w-full`} value={draft[s]} inputMode="decimal" placeholder="—" autoComplete="off" disabled={disabled}
              aria-invalid={bad || undefined} onChange={(e) => onChange({ ...draft, [s]: e.target.value })} />
            <span className="text-casevo-text-soft" aria-hidden="true">%</span>
          </div>
        </div>
      )
    })}
  </div>
)

// Reduction order — up / down buttons, top gives way first.
export const ReduceOrderEditor: React.FC<{ order: DeductionItem[]; onChange: (o: DeductionItem[]) => void; disabled?: boolean }> = ({ order, onChange, disabled }) => {
  const move = (i: number, by: -1 | 1): void => {
    const j = i + by
    if (j < 0 || j >= order.length) return
    const next = [...order]
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }
  return (
    <ol className="m-0 p-0 list-none flex flex-col gap-[6px]">
      {order.map((k, i) => (
        <li key={k} className="flex items-center gap-[10px] px-[12px] py-[7px] rounded-[9px] border border-casevo-border-soft bg-casevo-input">
          <span className="w-5 text-[12px] font-bold text-casevo-text-soft tabular-nums">{i + 1}</span>
          <span className="flex-1 text-[13.5px] text-casevo-forest">{DEDUCTION_LABEL[k]}</span>
          <button type="button" className={S_BTN_GHOST_SM} disabled={disabled || i === 0} onClick={() => move(i, -1)} aria-label={`Move ${DEDUCTION_LABEL[k]} up`}>↑</button>
          <button type="button" className={S_BTN_GHOST_SM} disabled={disabled || i === order.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${DEDUCTION_LABEL[k]} down`}>↓</button>
        </li>
      ))}
    </ol>
  )
}

const SUB = 'The terms every new CFA starts with. Changing them never alters a CFA already on a case.'

// (Batch 2) The statement chase (backend: firm PI settlement defaults).
type Defaults = SettlementDefaults
export const CHASE_DAYS_DEFAULT = 14
// '14' → 14; anything but a whole number 1–90 → the message.
export const chaseDaysError = (text: string): string | null =>
  /^\d{1,2}$/.test(text.trim()) && Number(text) >= 1 && Number(text) <= 90 ? null : 'Enter a whole number of days from 1 to 90.'

const PiSettlementDefaultsPanel: React.FC = () => {
  const [loaded, setLoaded] = useState<Defaults | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [staged, setStaged] = useState(false)
  const [flat, setFlat] = useState('')
  const [stages, setStages] = useState<StageDraft>(stageDraftOf([]))
  const [terms, setTerms] = useState<Record<string, boolean>>({})
  const [cap, setCap] = useState('')
  const [capBase, setCapBase] = useState<DeductionCapBase>('DAMAGES_NET_OF_CRU')
  const [order, setOrder] = useState<DeductionItem[]>([])
  const [chase, setChase] = useState(String(CHASE_DAYS_DEFAULT))
  const [chaseServerErr, setChaseServerErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const seed = (d: Defaults): void => {
    setLoaded(d)
    setChase(String(d.statementChaseDays ?? CHASE_DAYS_DEFAULT)); setChaseServerErr(null)
    setStaged(d.defaultSuccessStages.length > 0)
    setFlat(bpToPercentText(d.defaultSuccessRateBasisPoints))
    setStages(stageDraftOf(d.defaultSuccessStages))
    setTerms(Object.fromEntries(CFA_TERM_SPECS.map((t) => [t.key, d[termKey(t.key)] as boolean])))
    setCap(bpToPercentText(d.defaultTotalCapBasisPoints))
    setCapBase(d.defaultTotalCapBase)
    setOrder(d.defaultTotalCapReduceOrder)
  }
  useEffect(() => {
    let cancelled = false
    getSettlementDefaultsApi()
      .then((d) => { if (!cancelled) seed(d) })
      .catch((e) => { if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load the PI settlement defaults') })
    return () => { cancelled = true }
  }, [])
  useEffect(() => {
    if (!saved) return
    const t = setTimeout(() => setSaved(false), 2000)
    return () => clearTimeout(t)
  }, [saved])

  if (loadError) return <SettingsCard icon="percent" title="PI settlements" sub={SUB}><p className="text-[13px] text-casevo-danger py-[13px]" role="alert">{loadError}</p></SettingsCard>
  if (!loaded) {
    // Placeholder in the form's own shape (no jump when it lands): intro,
    // success fee (choice + one field), 8 term rows, firm cap (2 fields +
    // 5-item order), footer.
    const bar = 'rounded-[8px] bg-casevo-border-soft motion-safe:animate-pulse'
    return (
      <SettingsCard icon="percent" title="PI settlements" sub={SUB}>
        <div aria-busy="true" aria-label="Loading PI settlement defaults">
          <div className={`${bar} h-[38px] mt-[4px]`} />
          <div className="pt-[16px]"><div className={`${bar} h-[18px] w-28`} /><div className={`${bar} h-[34px] mt-[4px]`} /><div className={`${bar} h-[32px] w-56 mt-[10px]`} /><div className={`${bar} h-[62px] w-40 mt-[12px]`} /></div>
          <div className="pt-[18px] mt-[14px] border-t border-casevo-border-soft"><div className={`${bar} h-[18px] w-24`} />
            {Array.from({ length: 8 }, (_, i) => <div key={i} className={`${bar} h-[58px] mt-[10px]`} />)}
          </div>
          <div className="pt-[18px] mt-[6px] border-t border-casevo-border-soft"><div className={`${bar} h-[18px] w-56`} /><div className={`${bar} h-[34px] mt-[4px]`} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px] pt-[12px]"><div className={`${bar} h-[80px]`} /><div className={`${bar} h-[80px]`} /></div>
            <div className={`${bar} h-[52px] mt-[14px]`} />
            {Array.from({ length: 5 }, (_, i) => <div key={i} className={`${bar} h-[44px] mt-[6px]`} />)}
          </div>
          <div className={`${bar} h-[36px] w-36 mt-[18px]`} />
        </div>
      </SettingsCard>
    )
  }

  // ── Diff-only patch + validation ──
  const patch: Partial<Defaults> = {}
  const flatBp = percentTextToBp(flat)
  const stageList = stagesOfDraft(stages)
  const capBp = percentTextToBp(cap)
  const flatError = !staged && flatBp === undefined ? 'Enter a percentage from 0 to 100, e.g. 25.' : null
  const stagesError = staged && (stageList === null ? 'Each rate must be a percentage from 0 to 100.' : stageList.length === 0 ? 'Enter at least one stage’s rate, or use one rate.' : null)
  const chaseError = chaseDaysError(chase)
  const capError = capBp === undefined ? 'Enter a percentage from 0 to 100, or leave it blank for no firm cap.' : null
  if (staged) {
    if (stageList && JSON.stringify(stageList) !== JSON.stringify(loaded.defaultSuccessStages)) patch.defaultSuccessStages = stageList
    if (loaded.defaultSuccessRateBasisPoints !== null) patch.defaultSuccessRateBasisPoints = null
  } else {
    if (flatBp !== undefined && flatBp !== loaded.defaultSuccessRateBasisPoints) patch.defaultSuccessRateBasisPoints = flatBp
    if (loaded.defaultSuccessStages.length) patch.defaultSuccessStages = []
  }
  for (const t of CFA_TERM_SPECS) {
    const k = termKey(t.key)
    if (terms[t.key] !== loaded[k]) (patch as Record<string, unknown>)[k] = terms[t.key]
  }
  if (capBp !== undefined && capBp !== loaded.defaultTotalCapBasisPoints) patch.defaultTotalCapBasisPoints = capBp
  if (capBase !== loaded.defaultTotalCapBase) patch.defaultTotalCapBase = capBase
  if (JSON.stringify(order) !== JSON.stringify(loaded.defaultTotalCapReduceOrder)) patch.defaultTotalCapReduceOrder = order
  if (!chaseError && Number(chase) !== (loaded.statementChaseDays ?? CHASE_DAYS_DEFAULT)) patch.statementChaseDays = Number(chase)
  const blocked = !!(flatError || stagesError || capError || chaseError)
  // Invalid text never reaches the patch, but it IS an unsaved change.
  const dirty = Object.keys(patch).length > 0 || blocked

  const save = async (): Promise<void> => {
    if (!dirty || blocked) return
    setSaving(true); setError(null)
    try { seed(await patchSettlementDefaultsApi(patch)); setSaved(true) } catch (e) {
      const data = (e as { response?: { data?: { error?: string; field?: string } } }).response?.data
      if (data?.field === 'statementChaseDays' && data.error) { setChaseServerErr(data.error); return }
      setError(data?.error ?? (e instanceof Error ? e.message : 'Failed to save the defaults'))
    } finally { setSaving(false) }
  }

  return (
    <SettingsCard icon="percent" title="PI settlements" sub={SUB}>
      <p className={`${S_NOTE} pt-[4px]`}>
        These are the firm’s standard CFA terms. Each one decides how much can be taken from a client’s damages at settlement. Options marked “deducts less” leave the client with more.
      </p>

      {/* Success rate */}
      <div className="pt-[16px]">
        <h3 className="text-[13.5px] font-semibold text-casevo-forest m-0">Success fee</h3>
        <p className={`${S_NOTE} mt-[2px]`}>The uplift on base costs the firm charges if the claim wins. A staged rate goes up as the case progresses; a stage left blank uses the rate before it.</p>
        <div role="radiogroup" aria-label="Success rate" className="flex flex-wrap gap-[8px] mt-[10px]">
          {[{ v: false, l: 'One rate' }, { v: true, l: 'Staged rates' }].map((o) => (
            <button key={o.l} type="button" role="radio" aria-checked={staged === o.v} disabled={saving} onClick={() => setStaged(o.v)}
              className={`h-[32px] px-[12px] rounded-[9px] border text-[13px] font-semibold focus:outline-none casevo-focus disabled:opacity-50 disabled:cursor-not-allowed ${staged === o.v ? 'border-casevo-mid bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8]' : 'border-casevo-border text-casevo-forest hover:border-casevo-mid'}`}>
              {o.l}
            </button>
          ))}
        </div>
        <div className="mt-[12px]">
          {staged ? (
            <>
              <StageRatesEditor draft={stages} onChange={setStages} disabled={saving} idPrefix="pi-def-stage" />
              {stagesError && <p className={S_FIELD_ERROR} role="alert">{stagesError}</p>}
            </>
          ) : (
            <div>
              <label className={S_LABEL} htmlFor="pi-def-rate">Success rate</label>
              <div className="flex items-center gap-[8px]">
                <input id="pi-def-rate" className={`${sInput(!!flatError)} w-[110px]`} value={flat} onChange={(e) => setFlat(e.target.value)} disabled={saving} inputMode="decimal" placeholder="e.g. 25" autoComplete="off" aria-invalid={!!flatError || undefined} />
                <span className="text-casevo-text-soft" aria-hidden="true">%</span>
              </div>
              {flatError ? <p className={S_FIELD_ERROR} role="alert">{flatError}</p> : <p className={`${S_NOTE} mt-[5px]`}>Applies at every stage. Blank = no default rate (set it on each CFA).</p>}
            </div>
          )}
        </div>
      </div>

      {/* Yes/No terms */}
      <div className="pt-[18px] mt-[14px] border-t border-casevo-border-soft">
        <h3 className="text-[13.5px] font-semibold text-casevo-forest m-0">CFA terms</h3>
        {CFA_TERM_SPECS.map((t) => {
          const on = !!terms[t.key]
          return (
            <SettingsToggleRow key={t.key}
              title={<span className="inline-flex flex-wrap items-center gap-[8px]">{t.title}{on === t.lessIs && <DeductsLess isDefault />}</span>}
              desc={<>{t.desc} <span className="text-casevo-forest">{t.lessIs ? 'On' : 'Off'} deducts less.</span></>}
              control={<SettingsSwitch checked={on} label={t.title} disabled={saving} onChange={() => setTerms({ ...terms, [t.key]: !on })} />} />
          )
        })}
      </div>

      {/* Firm total cap */}
      <div className="pt-[18px] mt-[6px] border-t border-casevo-border-soft">
        <h3 className="text-[13.5px] font-semibold text-casevo-forest m-0">Firm’s total cap on deductions</h3>
        <p className={`${S_NOTE} mt-[2px]`}>An overall limit the firm promises on everything it deducts, on top of the legal 25% cap on the success fee. Court-ordered costs (QOCS) are never reduced by it.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px] pt-[12px]">
          <div>
            <label className={S_LABEL} htmlFor="pi-def-cap">Total cap</label>
            <div className="flex items-center gap-[8px]">
              <input id="pi-def-cap" className={`${sInput(!!capError)} w-[110px]`} value={cap} onChange={(e) => setCap(e.target.value)} disabled={saving} inputMode="decimal" placeholder="None" autoComplete="off" aria-invalid={!!capError || undefined} />
              <span className="text-casevo-text-soft" aria-hidden="true">%</span>
            </div>
            {capError ? <p className={S_FIELD_ERROR} role="alert">{capError}</p> : <p className={`${S_NOTE} mt-[5px]`}>Blank = no firm cap. A lower percentage deducts less.</p>}
          </div>
          <div>
            <label className={S_LABEL} htmlFor="pi-def-base">Percentage of</label>
            <select id="pi-def-base" className={sInput(false)} value={capBase} onChange={(e) => setCapBase(e.target.value as DeductionCapBase)} disabled={saving}>
              {(Object.keys(CAP_BASE_LABEL) as DeductionCapBase[]).map((b) => <option key={b} value={b}>{CAP_BASE_LABEL[b]}{b === 'PSLA_AND_PAST_LOSS_NET_OF_CRU' ? ' (deducts less)' : ''}</option>)}
            </select>
            <p className={`${S_NOTE} mt-[5px]`}>What the cap is measured against. The smaller the figure, the less is deducted.</p>
          </div>
        </div>
        <div className="pt-[14px]">
          <span className={S_LABEL}>When a cap is reached, reduce in this order</span>
          <p className={`${S_NOTE} mb-[8px]`}>The item at the top gives way first. Used for the firm cap and the court’s limits for children.</p>
          <ReduceOrderEditor order={order} onChange={setOrder} disabled={saving} />
        </div>
      </div>

      {/* (Batch 2) Settlement statements: when to chase the client's answer */}
      <div className="pt-[18px] mt-[14px] border-t border-casevo-border-soft">
        <h3 className="text-[13.5px] font-semibold text-casevo-forest m-0">Settlement statements</h3>
        <p className={`${S_NOTE} mt-[2px]`}>How long to wait for the client’s answer to a sent statement before it’s flagged for chasing.</p>
        <div className="pt-[12px]">
          <label className={S_LABEL} htmlFor="pi-def-chase">Chase the client’s answer after</label>
          <div className="flex items-center gap-[8px]">
            <input id="pi-def-chase" className={`${sInput(!!(chaseError || chaseServerErr))} w-[80px]`} value={chase} disabled={saving} inputMode="numeric" autoComplete="off"
              aria-invalid={!!(chaseError || chaseServerErr) || undefined} aria-describedby="pi-def-chase-msg"
              onChange={(e) => { setChase(e.target.value); setChaseServerErr(null) }} />
            <span className="text-[13px] text-casevo-forest">days</span>
          </div>
          {chaseError || chaseServerErr
            ? <p id="pi-def-chase-msg" className={S_FIELD_ERROR} role="alert">{chaseError ?? chaseServerErr}</p>
            : <p id="pi-def-chase-msg" className={`${S_NOTE} mt-[5px]`}>From 1 to 90 days. Casevo’s default is {CHASE_DAYS_DEFAULT}.</p>}
        </div>
      </div>

      <div className={S_FORMFOOT}>
        <button type="button" className={S_BTN_PRIMARY} disabled={!dirty || blocked || saving} onClick={() => { void save() }}>{saving ? 'Saving…' : 'Save defaults'}</button>
        <UnsavedGuard dirty={dirty && !saving} />
        {dirty && !saving && <button type="button" className={S_BTN_GHOST} onClick={() => { seed(loaded); setError(null) }}>Discard changes</button>}
        {saved && <SettingsSaved />}
        {error && <span className="text-[12.5px] text-casevo-danger" role="alert">{error}</span>}
      </div>
    </SettingsCard>
  )
}

export default PiSettlementDefaultsPanel
